/**
 * @module ui/site-header--section
 */
import ee from "lib/eventemitter";
import { pageYOffset } from "utils/window-events";

const headerHiding = "c-site-header--section--hiding";
const headerHidden = "c-site-header--section--hidden";
const headerRevealing = "c-site-header--section--revealing";
const headerRevealed = "c-site-header--section--revealed"; // Default state
const headerSticky = "c-site-header--sticky";

let header; // element

/**
 * @method init
 * @description Additional initialization for this particular header type
 * @param {HTMLElement} headerElement - The header element
 * @param {Function} onScrollCallback - callback method for scroll
 * @param {Number} scrollUpThreshold - reveal header again after user has scrolled up past this pixel limit
 */
export default function init(headerElement, onScrollCallback, scrollUpThreshold) {
  header = headerElement;
  if (!header) {
    return;
  }

  const minimum = Math.floor(header.getBoundingClientRect().height);
  let total = 0; // track total distance scrolled (until buffer is limit is past)

  // store previous scroll position, to determine direction.
  let previousScrollPos = pageYOffset(); // current scroll position

  // header is revealed by default, but hide immediately if out of viewport.
  let scrollingBelowHeader = previousScrollPos > minimum;
  if (scrollingBelowHeader) {
    window.requestAnimationFrame(() => {
      header.classList.remove(headerRevealed);
      header.classList.add(headerHidden);
    });
  }

  const onPageScroll = scrollPos => {
    onScrollCallback(); // hide benefits menu
    const scrolled = Math.abs(previousScrollPos - scrollPos);
    if (scrolled <= 0) {
      return;
    }

    const scrollingDown = scrollPos >= previousScrollPos;
    scrollingBelowHeader = scrollPos > minimum;

    if (scrollingDown) {
      total = 0;
    } else {
      total += scrolled;
    }

    const scrollingUpwards = total >= scrollUpThreshold;

    window.requestAnimationFrame(() => {
      if (scrollingDown) {
        if (scrollingBelowHeader) {
          hideHeader();
        } else if (!scrollingBelowHeader) {
          header.classList.remove(headerSticky);
        }
      } else if (!scrollingBelowHeader || (scrollingBelowHeader && scrollingUpwards)) {
        header.classList.add(headerSticky);
        revealHeader();
      }
    });

    previousScrollPos = scrollPos;
  };
  ee.on("windowScroll.throttled", onPageScroll);

  header.addEventListener("animationend", onHeaderAnimationEnd);
}

/**
 * @method hideHeader
 * @description Hides the header by adding/removing various classes
 */
function hideHeader() {
  if (!header.classList.contains(headerHiding) && !header.classList.contains(headerHidden)) {
    // IE11 doesn't support multiple arguments for remove()
    header.classList.remove(headerRevealed);
    header.classList.remove(headerRevealing);
    header.classList.add(headerHiding);
  }
}

/**
 * @method revealHeader
 * @description Reveals the header by adding/removing various classes
 */
function revealHeader() {
  if (!header.classList.contains(headerRevealing) && !header.classList.contains(headerRevealed)) {
    // IE11 doesn't support multiple arguments for remove()
    header.classList.remove(headerHidden);
    header.classList.remove(headerHiding);
    header.classList.add(headerRevealing);
  }
}

/**
 * @method onHeaderAnimationEnd
 * @description Adds/removes various classes when the header has finished
 * animating between states
 * @param {Object} event - of the animation
 */
function onHeaderAnimationEnd(event) {
  if (event.animationName.indexOf("-header") !== -1) {
    // header events: reveal-header, hide-header
    if (header.classList.contains(headerHiding)) {
      header.classList.remove(headerHiding);
      header.classList.add(headerHidden);
      header.classList.remove(headerSticky);
    } else if (header.classList.contains(headerRevealing)) {
      header.classList.remove(headerRevealing);
      header.classList.add(headerRevealed);
    }
  }
}
